import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const focusableSelector =
  'a[href], area[href], input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), button:not([disabled]), iframe, object, embed, ' +
  '[contenteditable], [tabindex]:not([tabindex="-1"])';

const modalRootStyle = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '24px'
};

const overlayStyle = {
  position: 'absolute',
  inset: 0,
  background: 'rgba(10, 21, 35, 0.58)',
  backdropFilter: 'blur(3px)'
};

const closeButtonStyle = {
  border: 'none',
  borderRadius: '999px',
  width: '36px',
  height: '36px',
  background: '#eef2f8',
  color: '#233149',
  cursor: 'pointer',
  fontSize: '20px',
  lineHeight: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center'
};

/**
 * Accessible reusable modal using React Portal.
 * API: isOpen, onRequestClose, title, children, width, closeOnBackdrop
 */
const Modal = ({
  id,
  isOpen,
  onRequestClose,
  title,
  children,
  width = '720px',
  closeOnBackdrop = true
}) => {
  const reactId = useId();
  const titleId = `${id || `modal-${reactId}`}-title`;
  const dialogRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    previouslyFocusedRef.current = document.activeElement;

    const dialogElement = dialogRef.current;

    if (dialogElement) {
      const focusable = dialogElement.querySelectorAll(focusableSelector);
      if (focusable.length > 0) {
        focusable[0].focus();
      } else {
        dialogElement.focus();
      }
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onRequestClose();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const container = dialogRef.current;
      if (!container) {
        return;
      }

      const focusableElements = Array.from(container.querySelectorAll(focusableSelector)).filter(
        (element) => element.offsetParent !== null
      );

      if (focusableElements.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);

      if (
        previouslyFocusedRef.current &&
        typeof previouslyFocusedRef.current.focus === 'function'
      ) {
        previouslyFocusedRef.current.focus();
      }
    };
  }, [isOpen, onRequestClose]);

  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = '';
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  if (!isMounted || !isOpen) {
    return null;
  }

  return createPortal(
    <div className="modal-backdrop" style={modalRootStyle}>
      <div
        aria-hidden="true"
        style={overlayStyle}
        onClick={closeOnBackdrop ? onRequestClose : undefined}
      ></div>
      <div
        id={id || `modal-${reactId}`}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="modal-dialog"
        style={{
          position: 'relative',
          width: 'min(100%, ' + width + ')',
          maxHeight: 'min(92vh, 920px)',
          overflow: 'hidden',
          background: '#ffffff',
          borderRadius: '16px',
          boxShadow: '0 28px 70px -35px rgba(6, 20, 37, 0.95)',
          border: '1px solid rgba(16, 36, 66, 0.14)',
          transform: 'translateY(0)',
          animation: 'modal-enter 220ms ease'
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '14px',
            padding: '18px 20px',
            borderBottom: '1px solid #e6ebf2'
          }}
        >
          <h3 id={titleId} style={{ margin: 0, color: '#132033', fontSize: '1.1rem' }}>
            {title}
          </h3>
          <button
            type="button"
            style={closeButtonStyle}
            aria-label="Close experience details"
            onClick={onRequestClose}
          >
            ×
          </button>
        </header>
        <div style={{ padding: '20px', overflow: 'auto' }}>{children}</div>
      </div>

      <style>{`
        @keyframes modal-enter {
          from {
            opacity: 0;
            transform: translateY(10px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @media (max-width: 640px) {
          .modal-backdrop {
            padding: 12px;
            align-items: flex-end;
          }

          .modal-dialog {
            width: 100% !important;
            border-radius: 14px 14px 0 0 !important;
            max-height: 90vh !important;
          }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default Modal;
